import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useState } from "react";
import { z } from "zod";
import { Icon, Logo } from "@/components/ui";
import { anmelden, sitzungQuery } from "../auth";
import { sicheresZiel } from "../redirect";

/**
 * Die einzige Seite ohne Anmeldung — sie ist ja der Weg zu einer.
 *
 * Optik aus dem Prototyp (Marken-Panel links, Formular rechts), dahinter die
 * echte Anmeldung über BetterAuth. Die Rollen-Auswahl des Prototyps gibt es
 * hier nicht mehr: Die Rolle hängt perspektivisch am Konto, bis dahin lässt
 * sie sich nach dem Anmelden oben rechts umschalten.
 *
 * `?redirect-to=` merkt sich, wohin jemand eigentlich wollte, bevor der Guard
 * ihn hierher geschickt hat. Der Wert kommt aus der Adresszeile und wird
 * deshalb durch `sicheresZiel` geprüft, bevor irgendwohin gesprungen wird.
 */

/**
 * Der Bindestrich in `redirect-to` ist kein gültiger Bezeichner, der Zugriff
 * läuft also über `search["redirect-to"]`. Das ist der Name, der in der
 * Adresszeile steht, und der hat Vorrang vor der Bequemlichkeit im Code.
 */
const suchSchema = z.object({
	"redirect-to": z.string().optional(),
});

export const Route = createFileRoute("/login")({
	validateSearch: suchSchema,
	beforeLoad: async ({ context, search }) => {
		const sitzung = await context.queryClient.ensureQueryData(sitzungQuery);

		if (sitzung !== null) {
			// Wer schon angemeldet ist, hat auf dem Formular nichts verloren.
			throw redirect({ to: sicheresZiel(search["redirect-to"]) });
		}
	},
	component: AnmeldeSeite,
});

function AnmeldeSeite() {
	const { queryClient } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = useNavigate();

	const [email, setEmail] = useState("");
	const [passwort, setPasswort] = useState("");
	const [fehler, setFehler] = useState<string | null>(null);
	const [laeuft, setLaeuft] = useState(false);

	const absenden = async () => {
		setLaeuft(true);
		setFehler(null);

		try {
			const ergebnis = await anmelden({ queryClient, email, password: passwort });

			if (!ergebnis.ok) {
				setFehler(ergebnis.message);

				return;
			}

			// `anmelden` hat die Sitzung schon frisch in den Cache gelegt, der
			// Guard der Zielroute läuft also gegen den neuen Stand. Ein
			// `router.invalidate()` davor braucht es nicht — es würde nur den
			// Guard dieser Seite hier nochmal auslösen.
			await navigate({ to: sicheresZiel(search["redirect-to"]) });
		} finally {
			setLaeuft(false);
		}
	};

	return (
		<div className="min-h-dvh grid lg:grid-cols-[minmax(0,5fr)_minmax(0,4fr)] bg-bg">
			{/* Marken-Panel — der erste Wow-Moment (DESIGN.md §9): eine Serif-Zeile, viel Ruhe. */}
			<div
				className="relative hidden lg:flex flex-col justify-between bg-sidebar p-12 xl:p-16 overflow-hidden"
				style={{ backgroundImage: "var(--gradient-hero)" }}
			>
				<div className="relative">
					<Logo light gross />
				</div>
				<motion.div
					initial={{ opacity: 0, y: 10 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
					className="relative max-w-xl"
				>
					<span className="eyebrow text-white/60 mb-5">Mitgliederbereich</span>
					<h1 className="font-display text-white text-[40px] xl:text-[48px] leading-[1.08] font-medium tracking-tight text-balance">
						Eure Videos, Termine und Freigaben — an einem Ort.
					</h1>
					<p className="text-white/60 mt-6 text-body xl:text-h2 leading-relaxed max-w-md text-pretty">
						Hier läuft alles rund um eure Produktion mit beckel production zusammen: der Stand eures
						Projekts, Drehvorbereitung, fertige Inhalte und alles, was wir von euch brauchen.
					</p>
				</motion.div>
				<div className="relative flex flex-wrap items-center gap-x-6 gap-y-2 text-white/50 text-small">
					<span>Projektstand live</span>
					<span>Freigaben mit Zeitstempel</span>
					<span>Downloads jederzeit</span>
				</div>
			</div>

			{/* Formular-Panel */}
			<div className="flex items-center justify-center p-6 sm:p-12">
				<motion.div
					initial={{ opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
					className="w-full max-w-sm"
				>
					<div className="lg:hidden mb-10">
						<Logo />
					</div>
					<h2 className="font-display text-display font-medium text-ink">Willkommen</h2>
					<p className="text-body text-ink-soft mt-1.5">Melde dich mit deiner E-Mail-Adresse an.</p>

					<form
						className="mt-8"
						onSubmit={(event) => {
							event.preventDefault();
							void absenden();
						}}
					>
						<div className="space-y-4">
							<Feld
								id="email"
								label="E-Mail-Adresse"
								type="email"
								autoComplete="username"
								value={email}
								onChange={setEmail}
							/>
							<Feld
								id="passwort"
								label="Passwort"
								type="password"
								autoComplete="current-password"
								value={passwort}
								onChange={setPasswort}
							/>
						</div>

						{fehler === null ? null : (
							<p
								// `role="alert"` sorgt dafür, dass ein Screenreader die
								// Meldung vorliest, ohne dass der Fokus springt.
								role="alert"
								className="notice-negative mt-4"
							>
								<Icon name="x" size={15} className="text-negative shrink-0 mt-0.5" />
								<span>{fehler}</span>
							</p>
						)}

						<button type="submit" disabled={laeuft} className="btn-primary btn-lg w-full mt-6">
							{laeuft ? "Einen Moment …" : "Anmelden"}
						</button>
					</form>

					<div className="mt-6 flex flex-col items-center gap-1.5 text-caption text-ink-mute text-center">
						<Link
							to="/passwort-vergessen"
							className="font-medium text-ink underline decoration-ink/30 underline-offset-2 hover:decoration-ink"
						>
							Passwort vergessen?
						</Link>
						<span>Noch kein Zugang? Deinen Zugang legt beckel production für dich an.</span>
					</div>
				</motion.div>
			</div>
		</div>
	);
}

/**
 * Bleibt bewusst in dieser Datei: Das Feld wird nur vom Anmeldeformular
 * benutzt und hat außerhalb davon keinen Zweck.
 */
function Feld(props: {
	id: string;
	label: string;
	type: "email" | "password";
	autoComplete: string;
	value: string;
	onChange: (wert: string) => void;
}) {
	return (
		<label htmlFor={props.id} className="block">
			<span className="label">{props.label}</span>
			<input
				id={props.id}
				type={props.type}
				required
				autoComplete={props.autoComplete}
				value={props.value}
				onChange={(event) => {
					props.onChange(event.target.value);
				}}
				className="input"
			/>
		</label>
	);
}
